import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import FormField from "components/admin/FormField";
import ActionButton from "components/admin/ActionButton";
import RulesField from "./RulesField";
import { ordinal, toDate, winnerDisplayName } from "./giveawayFormUtils";
import { GiveawayContest, GiveawayWinner } from "types/giveaway";
import {
  getGiveawayContestById,
  getGiveawayWinners,
} from "apis/adminServices/giveawayServices";
import { useToastStore } from "store/toastStore";

const LIST_PATH = "/admin/giveaway-management";

// Sends the list page back to the "previous" tab, since this screen is only
// ever reached from there.
const LIST_STATE = { tab: "previous" as const };

/**
 * Read-only twin of the editable form's control styling — same box, no focus
 * or caret behaviour, since there is nothing to type into.
 */
const readOnlyClass =
  "w-full p-3 rounded-lg border border-[#D1D5DB] text-sm text-grease-100 break-words";

const formatDateTime = (value?: string | null) => {
  const date = toDate(value);
  return date ? format(date, "dd/MM/yyyy HH:mm") : "-";
};

/**
 * Details screen for a contest that has already run.
 *
 * Deliberately not the same screen as `GiveawayContestForm`: a finished contest
 * shows who won it, and drops the fields that only matter while a contest is
 * being promoted (Instagram/LinkedIn URLs, thumbnail). It is a
 * view — a concluded contest isn't edited from here, so nothing on the page
 * takes input and there is no save action.
 *
 * Mounted at `/admin/giveaway-management/:contestId/details` — the "Contest
 * Details" action on the Previous section of the list page.
 */
const GiveawayPastContestDetails: React.FC = () => {
  const { contestId } = useParams<{ contestId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToastStore();

  const [contest, setContest] = useState<GiveawayContest | null>(null);
  const [winners, setWinners] = useState<GiveawayWinner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!contestId) return;
    const id = Number(contestId);

    let active = true;
    setLoading(true);

    Promise.all([getGiveawayContestById(id), getGiveawayWinners(id)])
      .then(([loadedContest, contestWinners]) => {
        if (!active) return;

        if (!loadedContest) {
          showToast("Contest not found", "error", 3000);
          navigate(LIST_PATH, { replace: true, state: LIST_STATE });
          return;
        }

        setContest(loadedContest);
        setWinners(contestWinners);
      })
      .catch((error) => {
        console.error("Failed to load contest details:", error);
        if (active) showToast("Failed to load contest details", "error", 3000);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contestId]);

  return (
    <>
      {/* Page heading card */}
      <section className="bg-white px-4 py-5 md:px-6 md:py-6 rounded-xl flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(LIST_PATH, { state: LIST_STATE })}
            className="w-8 h-8 rounded-lg border border-[#D1D5DB] flex items-center justify-center text-grease-100 hover:bg-gray-50 transition-colors"
            title="Back to Giveaway Management"
            aria-label="Back to Giveaway Management"
          >
            <i className="st-arrow-left text-xs" aria-hidden="true" />
          </button>
          <h1 className="text-xl font-bold text-grease-100 tracking-tight">
            Giveaway Management
          </h1>
        </div>
      </section>

      <div className="bg-white p-4 md:p-6 rounded-xl mt-6">
        {loading ? (
          <p className="text-center py-12 text-lightgray-200 text-sm">
            Loading...
          </p>
        ) : (
          // No `grid-cols-1`: assets/css/style.css ships a stale unconditional
          // `.grid-cols-1` that loads after Tailwind and would beat
          // `md:grid-cols-2` at every width. A bare grid is already
          // single-column, so the override never gets a chance to bite.
          // Columns are explicit rather than row-major, since the right one
          // holds a variable number of winner slots.
          <div>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
              {/* --- Contest --- */}
              <div className="space-y-6">
                <FormField label="Contest Name">
                  <p className={readOnlyClass}>{contest?.contestName || "-"}</p>
                </FormField>

                <FormField label="Contest Reward 1st Prize">
                  <p className={readOnlyClass}>{contest?.reward1st || "-"}</p>
                </FormField>

                <FormField label="Contest Reward 2nd Prize">
                  <p className={readOnlyClass}>{contest?.reward2nd || "-"}</p>
                </FormField>

                <FormField label="Contest Reward 3rd Prize">
                  <p className={readOnlyClass}>{contest?.reward3rd || "-"}</p>
                </FormField>

                <div className="grid md:grid-cols-2 gap-x-6 gap-y-6">
                  <FormField label="Additional Winners Count">
                    <p className={readOnlyClass}>
                      {contest?.additionalWinnersCount ?? "-"}
                    </p>
                  </FormField>

                  <FormField label="Token Reward – Additional Winners">
                    <p className={readOnlyClass}>
                      {contest?.tokenRewardAdditionalWinners ?? "-"}
                    </p>
                  </FormField>
                </div>

                <FormField label="Contest Period">
                  <div className="flex flex-col md:flex-row gap-3">
                    <div className="flex-1 rounded-lg border border-[#D1D5DB] px-3 py-2">
                      <p className="text-xs font-semibold text-grease-100">
                        Start Date &amp; Time
                      </p>
                      <p className="text-sm text-grease-200">
                        {formatDateTime(contest?.startDate)}
                      </p>
                    </div>

                    <div className="flex-1 rounded-lg border border-[#D1D5DB] px-3 py-2">
                      <p className="text-xs font-semibold text-grease-100">
                        End Date &amp; Time
                      </p>
                      <p className="text-sm text-grease-200">
                        {formatDateTime(contest?.endDate)}
                      </p>
                    </div>
                  </div>
                </FormField>
              </div>

              {/* --- Winners and result --- */}
              <div className="space-y-6">
                {/* 1st/2nd/3rd Prize Winners */}
                {([1, 2, 3] as const).map((place) => {
                  const rewardKey =
                    place === 1 ? "reward1st" : place === 2 ? "reward2nd" : "reward3rd";
                  // 1st Prize is always shown; 2nd/3rd only if that reward was configured.
                  if (place !== 1 && !contest?.[rewardKey]?.trim()) return null;

                  const placeWinners = winners.filter(
                    (w) => !w.isAdditionalWinner && (w.position ?? (place === 1 ? 1 : 0)) === place,
                  );
                  const label = place === 1 ? "1st" : place === 2 ? "2nd" : "3rd";

                  return (
                    <FormField key={place} label={`Winner Name ${label} Prize`}>
                      <div className={readOnlyClass}>
                        {placeWinners.length === 0 ? (
                          <span className="text-gray-400 italic">Not Awarded</span>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {placeWinners.map((w) => (
                              <span
                                key={w.winnerId}
                                className="inline-flex items-center px-3 py-1 rounded-md bg-gray-100 text-sm font-medium text-grease-100"
                              >
                                {winnerDisplayName(w)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </FormField>
                  );
                })}

                {/* Additional Winners (if configured) */}
                {!!contest?.additionalWinnersCount && contest.additionalWinnersCount > 0 && (
                  <FormField label="Additional Winners">
                    <div className={readOnlyClass}>
                      {(() => {
                        const additionalWinners = winners.filter((w) => w.isAdditionalWinner);
                        if (additionalWinners.length === 0) {
                          return <span className="text-gray-400 italic">Not Awarded</span>;
                        }
                        return (
                          <div className="flex flex-wrap gap-2">
                            {additionalWinners.map((w) => (
                              <span
                                key={w.winnerId}
                                className="inline-flex items-center px-3 py-1 rounded-md bg-gray-100 text-sm font-medium text-grease-100"
                              >
                                {winnerDisplayName(w)}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </FormField>
                )}

                <FormField label="Rules">
                  <RulesField value={contest?.rules ?? ""} />
                </FormField>
              </div>
            </div>

            <div className="flex justify-end mt-8">
              <ActionButton
                variant="secondary"
                onClick={() => navigate(LIST_PATH, { state: LIST_STATE })}
              >
                Cancel
              </ActionButton>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default GiveawayPastContestDetails;
